import { makeAutoObservable, runInAction } from 'mobx';
import CreateRmaService, {
    ICreateRMAResponse,
    IShouldRequestManagersApproval,
} from '../../../services/api/create-rma-service';
import { rootContainer, TYPE } from '../../../services/container/root';
import currentPortalStore from '../../../stores/current-portal-store';
import currentAccountStore from '../../accounts/stores/current-account-store';
import managerApprovalModalStore from '../../../manager-approval-modal/manager-approval-modal-store';
import { IManagerCredentials } from '../../../manager-approval-modal/manager-credentials-stores';
import type { CreateRmaDraftStore } from './create-rma-draft-store';
import type CreateRmaSettingsStore from './create-rma-settings-store';
import { ICreateRmaDto } from './rma-request-store';
import {
    SUBMISSION_CREDENTIALS_DECLINED,
    SUBMISSION_ERROR,
    type ICreateRmaOutcome,
} from './create-rma-submission-types';

export class CreateRmaSubmissionStore {
    private _isSubmitting: boolean = false;
    private _credentials?: IManagerCredentials;
    private _credentialsPromise?: Promise<IManagerCredentials | undefined>;

    constructor(private _settingsStore: CreateRmaSettingsStore) {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get createRmaService(): CreateRmaService {
        return rootContainer.get<CreateRmaService>(TYPE.CreateRmaService);
    }

    public get isSubmitting(): boolean { return this._isSubmitting; }

    public async run(drafts: CreateRmaDraftStore[]): Promise<ICreateRmaOutcome[]> {
        runInAction(() => {
            this._isSubmitting = true;
            this._credentials = undefined;
            this._credentialsPromise = undefined;
        });

        const outcomes = await Promise.all(
            drafts.map((draft) => this._submitDraft(draft)),
        );

        runInAction(() => {
            this._isSubmitting = false;
        });

        return outcomes;
    }

    private async _submitDraft(draft: CreateRmaDraftStore): Promise<ICreateRmaOutcome> {
        draft.markSubmitting();

        await draft.requestStore.balancePreviewStore.checkPreview();
        if (draft.requestStore.balancePreviewStore.isCreateRmaBlocked) {
            draft.markFailed();
            return this._makeOutcome(
                draft,
                false,
                undefined,
                SUBMISSION_ERROR.CUSTOMER_PAYMENT_REQUIRED,
            );
        }

        const request = draft.requestModel();

        if (this._shouldRequestCredentials(request, draft)) {
            const credentials = await this._ensureCredentials();
            if (!credentials) {
                draft.resetFromSubmitting();
                return this._makeOutcome(
                    draft,
                    false,
                    undefined,
                    SUBMISSION_CREDENTIALS_DECLINED,
                );
            }
            request.manager_approval_credentials = credentials;
        }

        try {
            const response = await this._postCreateRma(request);

            if (response === SUBMISSION_CREDENTIALS_DECLINED) {
                draft.resetFromSubmitting();
                return this._makeOutcome(draft, false, undefined, SUBMISSION_CREDENTIALS_DECLINED);
            }

            const createResponse = response as ICreateRMAResponse;

            if (createResponse.success) {
                const rmaId = (createResponse.created_rma?.rma_code ?? '').replace('RMA', '');
                draft.markCreated();
                return this._makeOutcome(draft, true, rmaId, undefined, createResponse);
            }

            draft.markFailed();
            return this._makeOutcome(draft, false, undefined, createResponse.message, createResponse);
        } catch (error) {
            console.error(error);
            draft.markFailed();
            return this._makeOutcome(draft, false, undefined, SUBMISSION_ERROR.SUBMISSION_FAILED);
        }
    }

    /**
     * The server asks for manager approval only when the request carried no credentials, so one
     * credentialed retry is always enough. The attempt is capped rather than looped: the credentials
     * are cached for the run, so a repeated request would re-post the same payload without ever
     * prompting the merchant again — a tight loop with no user-driven exit.
     */
    private async _postCreateRma(
        request: ICreateRmaDto,
    ): Promise<ICreateRMAResponse | typeof SUBMISSION_CREDENTIALS_DECLINED> {
        let response = await this.createRmaService.createRma(request);

        if (this._shouldRequestManagersApproval(response)) {
            const credentials = await this._ensureCredentials();
            if (!credentials) {
                return SUBMISSION_CREDENTIALS_DECLINED;
            }
            request.manager_approval_credentials = credentials;
            response = await this.createRmaService.createRma(request);
        }

        if (this._shouldRequestManagersApproval(response)) {
            throw new Error('create-rma kept requesting manager approval after credentials were sent');
        }

        return response;
    }

    private _shouldRequestManagersApproval(
        response: ICreateRMAResponse | IShouldRequestManagersApproval,
    ): response is IShouldRequestManagersApproval {
        return 'request_manager_approval' in response;
    }

    private _shouldRequestCredentials(request: ICreateRmaDto, draft: CreateRmaDraftStore): boolean {
        if (!request.is_auto_approved) {
            return false;
        }

        if (!currentPortalStore.requireManagerApproval()) {
            return false;
        }

        if (currentAccountStore.hasManagersApprovePermissions()) {
            return false;
        }

        if (!currentAccountStore.hasPromptManagersApprovePermissions()) {
            return false;
        }

        return draft.requestStore.eligibilityStore.isOutOfPolicy;
    }

    private async _ensureCredentials(): Promise<IManagerCredentials | undefined> {
        if (this._credentials) {
            return this._credentials;
        }

        if (!this._credentialsPromise) {
            this._credentialsPromise = this._collectCredentials();
        }

        return this._credentialsPromise;
    }

    private async _collectCredentials(): Promise<IManagerCredentials | undefined> {
        const credentialsResult = await managerApprovalModalStore.collectCredentials();
        if (!credentialsResult.credentials) {
            return undefined;
        }

        this._credentials = credentialsResult.credentials;
        return this._credentials;
    }

    private _makeOutcome(
        draft: CreateRmaDraftStore,
        isCreated: boolean,
        rmaId?: string,
        errorMessage?: string,
        createResponse?: ICreateRMAResponse,
    ): ICreateRmaOutcome {
        const firstProduct = draft.requestStore.requestProducts[0];

        return {
            draftIndex: draft.index,
            fulfillmentLabel: draft.fulfillmentLabel,
            isCreated,
            rmaId,
            errorMessage,
            firstItemName: firstProduct?.product?.name ?? '',
            message: createResponse?.message,
            messageTitle: createResponse?.message_title,
            warningMessage: createResponse?.warning_message,
        };
    }
}
